import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { enableProdMode, ErrorHandler, Version } from "@angular/core";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { getMessaging, provideMessaging } from "@angular/fire/messaging";
import { bootstrapApplication } from "@angular/platform-browser";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { provideRouter, RouteReuseStrategy, withInMemoryScrolling, withRouterConfig } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { ANKAADIA_VERSION, GITSHASHORT, installDateTimeAndUTCDateReviver } from "@ankaadia/ankaadia-shared";
import {
  ApolloErrorHandler,
  apolloFactory,
  APP_SHA,
  APP_VERSION,
  FRONTEND_ENVIRONMENT_INJECTION_TOKEN,
  uiBlockInterceptor,
  UiBlockService,
  uiLanguageInterceptor,
  uiTimezoneInterceptor,
  versionInterceptor,
} from "@ankaadia/ankaadia-shared-frontend";
import { Ankaadia } from "@ankaadia/ui";
import { provideAuth0 } from "@auth0/auth0-angular";
import { ApplicationinsightsAngularpluginErrorService } from "@microsoft/applicationinsights-angularplugin-js";
import { provideFormlyCore } from "@ngx-formly/core";
import { provideApollo } from "apollo-angular";
import { provideSpinnerConfig } from "ngx-spinner";
import { ConfirmationService as OldConfirmationService } from "primeng-v17/api";
import { DialogService as OldDialogService } from "primeng-v17/dynamicdialog";
import { ConfirmationService, MessageService } from "primeng/api";
import { providePrimeNG } from "primeng/config";
import { DialogService } from "primeng/dynamicdialog";
import { MessageService as MessageServiceInterface } from "./app/features/message/message.service";
import { NewUiMessageService } from "./app/features/message/new-ui-message.service";
import { OldUiMessageService } from "./app/features/message/old-ui-message.service";
import { formlyConfig } from "./app/formly/formly.config";
import { newUiFormlyConfig } from "./app/formly/new-ui-formly.config";
import { ApolloErrorHandlerService } from "./app/shared/apollo/apollo-error-handler.service";
import { possibleApolloReturnTypes } from "./app/shared/apollo/apollo-return-types";
import { GlobalSpinnerService } from "./app/shared/global-spinner/global-spinner.service";
import { NewUiGlobalSpinnerService } from "./app/shared/global-spinner/new-ui-global-spinner.service";
import { authInterceptor, isNoLoginOperation } from "./app/shared/interceptors/auth.interceptor";
import { pendingRequestsInterceptor } from "./app/shared/interceptors/pending-requests.interceptor";
import { updateRecognizerInterceptor } from "./app/shared/interceptors/updateRecognizer.interceptor";
import "./app/shared/primeng/new-ui-autocomplete-fix/new-ui-autocomplete-fix";
import "./app/shared/primeng/new-ui-picklist-fix/new-ui-picklist-fix";
import { AppRouteReuseStrategy } from "./app/shared/services/route-reuse-strategy.service";
import { StaticDataPipe } from "./app/shared/static-data/static-data.pipe";
import { SupportedTablePipes } from "./app/shared/table/table.model";
import { provideTransloco } from "./app/shared/transloco/transloco.provider";
import { AppComponent } from "./app/structure/app.component";
import { routes } from "./app/structure/app.routes";
import { NewUiAppComponent } from "./app/structure/new-ui-app.component";
import { environment } from "./environments/environment";

installDateTimeAndUTCDateReviver();

if (environment.production) {
  enableProdMode();
}

// Before bootstrapping the application, we check if the URL contains "clearLocalStorage=true".
// In this case, we clear all Auth0 tokens from localStorage in order to ensure re-authentication.
// This is required for ux switching
if (document.defaultView.location.href.endsWith("clearLocalStorage=true")) {
  Object.keys(localStorage)
    .filter((k) => k.startsWith("@@auth0spajs@@"))
    .forEach((k) => localStorage.removeItem(k));
  document.defaultView.location.href = document.defaultView.location.href.replace("clearLocalStorage=true", "");
}

bootstrapApplication(environment.newUI ? NewUiAppComponent : AppComponent, {
  providers: [
    provideAnimationsAsync(),

    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: "enabled" }),
      withRouterConfig({
        onSameUrlNavigation: "reload",
        paramsInheritanceStrategy: "always",
      })
    ),
    { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },

    provideHttpClient(
      withInterceptors([
        uiBlockInterceptor, // Important to be the first
        pendingRequestsInterceptor, // track all requests after UI block
        authInterceptor, // Auth Interceptor emits two requests, but apparently cancelles one directly
        versionInterceptor,
        uiLanguageInterceptor,
        uiTimezoneInterceptor,
        updateRecognizerInterceptor,
      ])
    ),

    ...(environment.newUI
      ? [
          providePrimeNG({
            overlayOptions: {
              appendTo: "body",
            },
            theme: {
              preset: Ankaadia,
              options: {
                darkModeSelector: ".dark",
                cssLayer: {
                  name: "primeng",
                  order: "theme, base, vendor, primeng",
                },
              },
            },
          }),
          ConfirmationService,
          DialogService,
          MessageService,
          ...SupportedTablePipes,
        ]
      : []),

    provideServiceWorker("ngsw-worker.js", {
      enabled: environment.useServiceWorker,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: "registerWhenStable:30000",
    }),

    provideAuth0({
      clientId: environment.auth0_clientId,
      domain: environment.auth0_domain,
      errorPath: "/error",
      skipRedirectCallback: false,
      // Persist tokens in localStorage so the session survives page reloads.
      // Without this, loginWithPopup only stores tokens in memory (lost on reload).
      // useRefreshTokens enables refresh-token-based silent re-auth as a reliable
      // alternative to iframe-based silent auth (which breaks when third-party cookies
      // are blocked, as is common in modern browsers).
      useRefreshTokens: true,
      // Fall back to iframe-based silent auth for sessions that were created before
      // refresh tokens were enabled (i.e. no refresh token in cache yet). Without
      // this fallback, those sessions emit a "Missing Refresh Token" error on
      // getAccessTokenSilently calls (e.g. during org-switch token cache bust).
      useRefreshTokensFallback: true,
      cacheLocation: "localstorage",
      authorizationParams: {
        audience: environment.auth0_audience,
        redirect_uri: window.location.origin + "/callback",
      },
      httpInterceptor: {
        allowedList: [environment.graphqlURI],
      },
    }),

    ...(!environment.newUI ? [provideSpinnerConfig({ type: "ball-scale-multiple" })] : []),

    provideTransloco(),

    { provide: StaticDataPipe },
    provideApollo(apolloFactory(environment, possibleApolloReturnTypes, isNoLoginOperation)),
    { provide: ApolloErrorHandler, useClass: ApolloErrorHandlerService },
    { provide: UiBlockService, useExisting: environment.newUI ? NewUiGlobalSpinnerService : GlobalSpinnerService },
    { provide: MessageServiceInterface, useExisting: environment.newUI ? NewUiMessageService : OldUiMessageService },

    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideMessaging(() => getMessaging()),

    provideFormlyCore(environment.newUI ? newUiFormlyConfig : formlyConfig),

    OldConfirmationService,
    OldDialogService,

    { provide: ErrorHandler, useClass: ApplicationinsightsAngularpluginErrorService }, // must be a singleton and therefor placed in the app module

    { provide: FRONTEND_ENVIRONMENT_INJECTION_TOKEN, useValue: environment },
    { provide: APP_VERSION, useValue: new Version(ANKAADIA_VERSION) },
    { provide: APP_SHA, useValue: GITSHASHORT },
  ],
})
  // eslint-disable-next-line no-console
  .catch((err) => console.error(err));
